import { useEffect, useMemo, useState } from "react";
import {
  fetchClientsForScope,
  fetchAipChangesAll,
  fetchAipChangesAllForScope,
  fetchCareTriageWorkflows,
  getUnityDualSiteLocationIds,
  shouldUseUnityCompanyClientScope,
} from "../utils/api";
import {
  filterCareHubClients,
  computeSummaryFromCases,
  buildTriageCases,
  mergeChangeResultsFromLocations,
  applyWorkflowOverlay,
  applyUnityFlemingtonTriageDemo,
} from "../utils/careTriageModel";

const VALID_RANGES = new Set(["today", "week", "month"]);

export function normalizeCareHubRange(range) {
  const value = String(range || "week").toLowerCase();
  return VALID_RANGES.has(value) ? value : "week";
}

export function useCareHubData(activeLocation, locations = [], changeRange = "week") {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allClients, setAllClients] = useState([]);
  const [changePayload, setChangePayload] = useState(null);
  const [workflows, setWorkflows] = useState([]);

  const range = normalizeCareHubRange(changeRange);

  const activeLocationId = useMemo(
    () => String(activeLocation?._id || activeLocation?.id || "").trim(),
    [activeLocation],
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!activeLocationId) {
        setAllClients([]);
        setChangePayload(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const locId = activeLocationId;
      const activeCompanyId =
        activeLocation?.companyId?._id ||
        activeLocation?.companyId?.id ||
        activeLocation?.companyId;

      try {
        const siblings =
          activeCompanyId && locations
            ? locations.filter((loc) => {
                const cid =
                  loc.companyId?._id || loc.companyId?.id || loc.companyId;
                return cid && cid.toString() === activeCompanyId?.toString();
              })
            : [];

        const useUnityScope = shouldUseUnityCompanyClientScope(activeLocation);
        const useCompanyScope =
          useUnityScope ||
          (activeCompanyId &&
            siblings.length > 1 &&
            (!activeLocation.clientCount || activeLocation.clientCount === 0));

        const changeLocationIds = useUnityScope
          ? getUnityDualSiteLocationIds(activeLocation, locations)
          : useCompanyScope
            ? siblings.map((loc) => String(loc._id || loc.id))
            : [locId];

        const workflowLocationIds = useUnityScope
          ? changeLocationIds
          : [locId];

        const changesPromise = useUnityScope
          ? fetchAipChangesAllForScope(activeLocation, locations, range).then(
              (payload) => [payload],
            )
          : Promise.all(
              changeLocationIds.map((id) => fetchAipChangesAll(id, range)),
            );

        const [clients, changeResponses, ...workflowBatches] = await Promise.all([
          fetchClientsForScope(activeLocation, locations),
          changesPromise,
          ...workflowLocationIds.map((id) =>
            fetchCareTriageWorkflows(id).catch(() => []),
          ),
        ]);
        const workflowRows = workflowBatches.flat();

        if (!active) return;
        setAllClients(Array.isArray(clients) ? clients : []);
        setWorkflows(Array.isArray(workflowRows) ? workflowRows : []);
        setChangePayload({
          range,
          results: mergeChangeResultsFromLocations(changeResponses),
          totalMatching: changeResponses.reduce(
            (sum, p) => sum + (p?.totalMatching ?? 0),
            0,
          ),
          locationScope: useUnityScope
            ? "unity-flemington-princeton"
            : useCompanyScope
              ? "company"
              : "location",
        });
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Failed to load care data");
        setAllClients([]);
        setChangePayload(null);
        setWorkflows([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [activeLocation, activeLocationId, locations, range]);

  const clients = useMemo(
    () => filterCareHubClients(allClients),
    [allClients],
  );

  const changeResults = changePayload?.results || [];

  const triageCases = useMemo(() => {
    const clientById = new Map(
      clients.map((c) => [String(c._id || c.id || "").trim(), c]).filter(([id]) => id),
    );
    // Same active/monitored roster as the "Active Clients" stat — exclude WellSky-deactivated clients.
    const base = buildTriageCases(clients, changeResults, {
      activeLocationId: activeLocationId,
    });
    const withWorkflow = applyWorkflowOverlay(base, workflows);
    // Re-apply after workflow so Flemington demo bucket/source always wins.
    return applyUnityFlemingtonTriageDemo(withWorkflow, clientById, {
      activeLocationId: activeLocationId,
    });
  }, [clients, changeResults, workflows, activeLocationId]);

  const reloadWorkflows = async () => {
    if (!activeLocationId) return;
    try {
      const ids = shouldUseUnityCompanyClientScope(activeLocation)
        ? getUnityDualSiteLocationIds(activeLocation, locations)
        : [activeLocationId];
      const batches = await Promise.all(
        ids.map((id) => fetchCareTriageWorkflows(id).catch(() => [])),
      );
      setWorkflows(batches.flat());
    } catch {
      /* keep prior */
    }
  };

  const summary = useMemo(
    () => computeSummaryFromCases(clients.length, triageCases),
    [clients.length, triageCases],
  );

  return {
    loading,
    error,
    clients,
    changeResults,
    changeMeta: changePayload,
    summary,
    triageCases,
    workflows,
    reloadWorkflows,
  };
}
